import { Component } from '@angular/core';
import { WebsiteAuditService } from '../website-audit.service';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { GoogleChartInterface } from 'ng2-google-charts';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-diagnostic',
  templateUrl: './diagnostic.component.html',
  styleUrls: [
    '../../diagnostics/detailed/detailed.component.css',
    './diagnostic.component.css',
  ],
})
export class DiagnosticComponent {
  constructor(
    private websiteAuditService: WebsiteAuditService,
    private notificationService: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  private subs: Subscription = new Subscription();

  private selectedTask: any = null;

  public firstLoad: boolean = false;
  public isLoading: boolean = false;

  public skeletonNumbers: any = [-1, -2, -3, -4, -5];

  public taskErrorsData: any = null;
  public pages: any = [];
  public errors: any = [];

  public pieChartLegend: any = [];
  public selectedType: string = 'overall';
  public pageFilterActive: string = 'overall';
  public types: any = [];

  public sortKey: string = 'impact';
  public sortOrder: string = 'desc';

  public token: string = '';
  public isSharableComponent: boolean = false;

  ngOnInit(): void {
    if (this.router.url.includes('/share/website-audit')) {
      this.token = this.route.snapshot.paramMap.get('token') as string;
      this.isSharableComponent = true;
    }

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((res: any) => {
        this.selectedTask = res;
        this.getAuditTaskErrors();
      }),
    );

    this.subs.add(
      this.websiteAuditService.getAuditTasksLoading().subscribe((res: any) => {
        this.isLoading = res;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  categoryChanged(event: any) {
    const selectedType = event?.['selectedRowValues']?.[0]?.split(':')[0];

    if (this.selectedType != selectedType) {
      this.typeChanged(selectedType);
    } else {
      this.typeChanged('overall');
    }
  }

  private getAuditTaskErrors(): void {
    if (!this.selectedTask && !this.isSharableComponent) {
      return;
    }

    const params: any = {
      ...(this.isSharableComponent
        ? { token: this.token }
        : { taskId: this.selectedTask.taskId }),
    };

    if (this.selectedType != 'overall') {
      params['type'] = this.selectedType;
    }

    if (this.pageFilterActive != 'overall') {
      params['category'] = this.pageFilterActive;
    }

    if (this.sortKey !== '') {
      params[this.sortKey] = this.sortOrder;
    }

    this.websiteAuditService.updateAuditTasksLoading(true);
    this.websiteAuditService.getAuditTaskErrors(params).subscribe({
      next: (res: any) => {
        this.firstLoad = true;
        this.websiteAuditService.updateAuditTasksLoading(false);

        this.taskErrorsData = res || null;

        this.updatePieBugChart();
        this.constructData();
      },
      error: (err: any) => {
        this.firstLoad = true;
        this.websiteAuditService.updateAuditTasksLoading(false);
        this.taskErrorsData = null;
        this.notificationService.error(err?.error?.message);
      },
    });
  }

  constructData(): void {
    // pages
    try {
      const pageKeys = Object.keys(
        this.taskErrorsData?.errorStats?.countByPage || {},
      );
      if (pageKeys.length == 0) {
        this.pages = [
          {
            value: 0,
            name: 'Product List',
          },
          {
            value: 0,
            name: 'Product Page',
          },
        ];
        this.skeletonNumbers = [1, 2, 3, 4, 5];
      } else {
        let highestValue = 0;

        this.pages = [];
        pageKeys.forEach((page: any) => {
          const value = Number(
            this.taskErrorsData?.errorStats?.countByPage[page],
          );

          if (value > highestValue) {
            highestValue = value;
          }

          this.pages.push({
            value: value,
            name: page,
          });
        });

        this.pages.sort((a: any, b: any) => b.value - a.value);

        let biggestValue = 0;
        this.skeletonNumbers = [];
        for (let i = 1; i <= 5; i++) {
          this.skeletonNumbers.push(Math.ceil(highestValue / 5) * i);
        }
        biggestValue = this.skeletonNumbers[this.skeletonNumbers.length - 1];

        this.pages.forEach((page: any) => {
          page.height = 0;
          page.width = 0;

          page.RLPercentage = (page.value / biggestValue) * 100;

          if (window.innerWidth > 900) {
            page.height = page.RLPercentage.toFixed(2) + '%';
            page.width = '';
          } else {
            page.height = '';
            page.width = page.RLPercentage.toFixed(2) + '%';
          }
        });
      }
    } catch (e) {
      console.log(e);
    }

    // types
    try {
      const types = Object.keys(
        this.taskErrorsData?.errorStats?.countByType || {},
      );
      this.types = types || [];
    } catch (e) {
      console.log(e);
    }

    // errors
    try {
      this.errors = this.taskErrorsData?.errors || [];
    } catch (e) {
      console.log(e);
    }
  }

  updatePieBugChart() {
    function formatNumber(num: any) {
      num = Math.abs(num);
      return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }
    const capitalizeFirstLetter = (str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const countByType = this.taskErrorsData?.errorStats?.countByType;

    let pieChartData: any = [];

    // populate pieChartData
    Object.keys(countByType).forEach((key) => {
      pieChartData.push([
        key + ':' + formatNumber(countByType[key]),
        countByType[key],
        "<p class='tooltip-text bugpiechart-tooltip'><b>" +
          capitalizeFirstLetter(key) +
          ': </b>' +
          formatNumber(countByType[key]) +
          '</p>',
      ]);
    });

    // sort from lowest to highest
    pieChartData.sort((a: any, b: any) => {
      if (a[1] < b[1]) {
        return 1;
      } else if (a[1] > b[1]) {
        return -1;
      }
      return 0;
    });

    // check if all elements have value
    if (pieChartData.length == 0) {
      // add gray color
      this.pieBugChart.options['colors'] = [
        '#A8A8A8',
        '#A9A9A9',
        '#B0B0B0',
        '#B8B8B8',
        '#BEBEBE',
        '#C0C0C0',
        '#C8C8C8',
        '#D0D0D0',
        '#D3D3D3',
      ];

      pieChartData.push(['No data found', 1, '']);
      pieChartData.push(['', 1, '']);
      pieChartData.push(['', 1, '']);
      pieChartData.push(['', 1, '']);
      pieChartData.push(['', 1, '']);
      this.pieBugChart.options['tooltip'].trigger = 'none';
    } else {
      this.pieBugChart.options['tooltip'].trigger = 'hover';
      // reset the colors
      this.pieBugChart.options['colors'] = [
        '#E1FFCA',
        '#CAECFF',
        '#5E4FFF',
        '#C8C3FF',
        '#4DA18C',
        '#0099c6',
        '#dd4477',
        '#66aa00',
        '#b82e2e',
      ];
    }

    // add first element
    pieChartData.unshift([
      'Types',
      'Value',
      { role: 'tooltip', p: { html: true } },
    ]);

    this.pieBugChart.dataTable = pieChartData;
    this.pieBugChart.component?.draw(this.pieBugChart);
    this.updatePieChartLegend();
  }

  updatePieChartLegend() {
    const capitalizeFirstLetter = (str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    this.pieChartLegend = [];

    for (let i = 0; i < this.pieBugChart.dataTable.length - 1; i++) {
      if (this.pieBugChart.dataTable[i + 1][0]?.split(':')[0] == '') continue;

      let valueTmp = this.pieBugChart.dataTable[i + 1][0]?.split(':')[1];

      this.pieChartLegend.push({
        name: capitalizeFirstLetter(
          this.pieBugChart.dataTable[i + 1][0]?.split(':')[0],
        ),
        value: valueTmp,
        color: this.pieBugChart.options['colors'][i],
      });
    }
  }

  public sortData(sortKey: string): void {
    if (this.sortKey === sortKey) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = sortKey;
      this.sortOrder = 'desc';
    }

    this.getAuditTaskErrors();
  }

  typeChanged(event: any) {
    this.selectedType = event?.value || event;

    if (this.selectedType != '') {
      this.getAuditTaskErrors();
    }
  }

  pageChangedFilter(bugPage: string) {
    this.pageFilterActive = bugPage;
    this.getAuditTaskErrors();
  }

  clearFilters() {
    this.pageFilterActive = 'overall';
    this.selectedType = 'overall';
    this.getAuditTaskErrors();
  }

  onResize(event: any) {
    // Rebuild charts so they are responsive
    this.pieBugChart?.component?.draw();
  }

  public pieBugChart: GoogleChartInterface = {
    chartType: 'PieChart',
    dataTable: [],
    options: {
      pieSliceText: 'none',
      pieHole: 0.6,
      sliceVisibilityThreshold: 0,
      //focusTarget: 'category',
      colors: [
        '#3366cc',
        '#dc3912',
        '#ff9900',
        '#109618',
        '#990099',
        '#0099c6',
        '#dd4477',
        '#66aa00',
        '#b82e2e',
      ],
      legend: {
        position: 'none',
        maxLines: 2,
        alignment: 'center',
        textStyle: {
          fontSize: 11,
          fontName: 'Roboto, sans-serif',
          color: '#666',
        },
      },
      height: window.innerWidth < 1445 ? 160 : 220,
      tooltip: {
        textStyle: {
          fontSize: 11,
        },
        isHtml: true,
      },
      animation: {
        startup: true,
        duration: 600,
        easing: 'out',
      },
      chartArea: {
        height: '99%',
        top: 10,
        left: 10,
        right: 10,
        bottom: 10,
        width: '99%',
      },
    },
  };
}
